import React, { useRef } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { TimelineGtiSlideFrame } from './timelineGtiSlideFrame';
import ScrollHintArrow from '../ScrollHintArrow';

gsap.registerPlugin(ScrollTrigger);

// ---------- Styled Components ----------

const ComparisonSection = styled.section`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
`;

const BaseSlide = styled.div<{ bg: string }>`
  position: absolute;
  width: 100%;
  height: 100%;
  background: ${({ bg }) => bg};
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 6vw;
  font-weight: bold;
`;

const Slide = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
  top: 0;
  left: 0;
`;

const SlideInner = styled.div<{ bg: string }>`
  width: 100%;
  height: 100%;
  background: ${({ bg }) => bg};
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 6vw;
  font-weight: bold;
`;

export const TimelineGti: React.FC = () => {
  // ✅ Slide data
  const slides = [
    {
      label: 'MK1',
      year: 1976,
      power: 110,
      title: 'Una leyenda que nació en secreto',
      subtitle:
        'Un proyecto oculto que dio vida al “Sport Golf”. Lo que serían solo 5.000 unidades se convirtió en un éxito de 461.690, con detalles icónicos como la tapicería tartán y el pomo en forma de pelota de golf.',
      bgColor: '#000000',
      color: '#C90010',
    },
    {
      label: 'MK2',
      year: 1984,
      power: 136,
      title: 'Creció en tamaño\n creció en potencia',
      subtitle:
        'Sus motores deportivos aseguraron el éxito de esta edición con la llegada de la versión 16V de 136 CV y también el G60, un 8 vávulas con compresor G-Lader y 160 CV de pura adrenalina. Esta versión gustó tanto, que en 1990 superó el millón de unidades.',
      bgColor: '#C90010',
      color: '#FFFFFF',
    },
    {
      label: 'MK3',
      year: 1991,
      power: 150,
      title: 'Un exterior más suave y redondeado',
      subtitle:
        'Y mejoró en seguridad con el doble airbag. Además, fue el primer GTI con una edición 20 aniversario, con llantas BBS RS2 y pinzas de frenos visibles en rojo.',
      bgColor: '#000000',
      color: '#C90010',
    },
    {
      label: 'MK4',
      year: 1997,
      power: 180,
      title: 'Una revolución cualitativa en acabados',
      subtitle:
        'El primer Golf GTI en incorporar motores turbo de gasolina. Dos versiones de 150 CV y 180 CV que marcaron una nueva era en rendimiento.',
      bgColor: '#C90010',
      color: '#FFFFFF',
    },
    {
      label: 'MK5',
      year: 2003,
      power: 200,
      title: 'Diseño icónico:\n parrilla vertical',
      subtitle:
        'Destacó su parrilla vertical con rejilla tipo honeycomb. Además, alcanzaba los 100 km/h en menos de 7 segundos gracias a ser el primer GTI en equipar la caja de cambios DSG.',
      bgColor: '#000000',
      color: '#C90010',
    },
    {
      label: 'MK6',
      year: 2008,
      power: 235,
      title: 'Motor, suspensión y chasis en evolución',
      subtitle:
        'El primero con suspensión adaptativa DCC y diferencial electrónico XDS. Mantuvo detalles icónicos como el pomo de cambio de pelota de golf y celebró su 35 aniversario con una edición especial de 235CV.',
      bgColor: '#C90010',
      color: '#FFFFFF',
    },
    {
      label: 'MK7',
      year: 2013,
      power: 245,
      title: 'Nuevas potencias desbloqueadas',
      subtitle:
        'La versión más destacada fue el GTI Clubsport S, con 310CV y un nuevo récord en el circuito de Nürburgring: 7:49:21’.',
      bgColor: '#000000',
      color: '#C90010',
    },
    {
      label: 'MK8',
      year: 2020,
      power: 265,
      title: 'Alcanzó su máximo rendimiento',
      subtitle: 'Con 195kW (265CV), bajó por primera vez de los 6s para llegar a los 100km/h.',
      bgColor: '#C90010',
      color: '#FFFFFF',
    },
  ];

  const sectionRef = useRef<HTMLDivElement>(null);
  const wrapperRefs = useRef<(HTMLDivElement | null)[]>([]);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);

  useGSAP(() => {
    if (!sectionRef.current) return;

    const numTransitions = slides.length - 1;
    const scrollPerSlide = 1.3; // Multiplicador para que cada slide tenga más espacio
    const totalScroll = numTransitions * scrollPerSlide * window.innerWidth;

    const tl = gsap.timeline({
      scrollTrigger: {
        id: 'timeline-gti-pin', // 👈 id único del pin
        trigger: sectionRef.current,
        start: 'top top',
        end: `+=${totalScroll}`,
        scrub: true,
        pin: true,
        anticipatePin: 1,
      },
      defaults: { ease: 'none' },
    });

    for (let i = 1; i < slides.length; i++) {
      const wrapper = wrapperRefs.current[i];
      const content = contentRefs.current[i];
      if (!wrapper || !content) continue;

      const offset = (i - 1) * (scrollPerSlide * 0.45);

      tl.fromTo(wrapper, { xPercent: 100 }, { xPercent: 0, duration: 0.5 }, offset);
      tl.fromTo(content, { xPercent: -100 }, { xPercent: 0, duration: 0.5 }, offset);
    }
  }, []);

  return (
    <ComparisonSection ref={sectionRef}>
      {/* Primer slide visible desde el inicio */}
      <BaseSlide bg={slides[0].color}>
        <TimelineGtiSlideFrame
          label={slides[0].label}
          year={slides[0].year}
          power={slides[0].power}
          title={slides[0].title}
          subtitle={slides[0].subtitle}
          bgColor={slides[0].bgColor}
          color={slides[0].color}
        />
      </BaseSlide>

      {/* Slides siguientes que se revelan */}
      {slides.slice(1).map((slide, i) => (
        <Slide
          key={i}
          ref={(el) => {
            wrapperRefs.current[i + 1] = el;
          }}
        >
          <SlideInner
            ref={(el) => {
              contentRefs.current[i + 1] = el;
            }}
            bg={slide.color}
          >
            <TimelineGtiSlideFrame
              label={slide.label}
              year={slide.year}
              power={slide.power}
              title={slide.title}
              subtitle={slide.subtitle}
              bgColor={slide.bgColor}
              color={slide.color}
            />
          </SlideInner>
        </Slide>
      ))}
      <ScrollHintArrow pinId="timeline-gti-pin" />
    </ComparisonSection>
  );
};
